import { PAN_SIZE } from '../constants';
import {
    BankCard,
    BankCardNumber,
    FrameApplePayIncomingMessageType,
    FrameMessageType,
    FrameStartApplePayMessage,
    PaymentMessage,
    PaymentMethodType,
    SbpToken,
} from '../types';

const VALID_DOMAIN_SFX = ['ru', 'com', 'com.tr', 'kz', 'by', 'uz', 'kg'];

export function getDomainSfx(): string {
    const validHostnameRe = /(^|.+\.)yandex\.(.+)$/;

    if (typeof window !== 'undefined' && window && window.location && window.location.hostname) {
        const match = window.location.hostname.match(validHostnameRe);

        if (match && VALID_DOMAIN_SFX.includes(match[2]!)) {
            return match[2]!;
        }
    }

    return 'ru';
}

export function fixDomainSfx(url: string, domainSfx: string) {
    if (!VALID_DOMAIN_SFX.includes(domainSfx)) {
        return url;
    }

    const validUrl = /^(https:\/\/trust(-test)?\.yandex)\.ru\//;

    return url.replace(validUrl, `$1.${domainSfx}/`);
}

const VALID_LANG_CODES = [
    'az',
    'be',
    'en',
    'et',
    'fi',
    'he',
    'hy',
    'ka',
    'kk',
    'ky',
    'lv',
    'ro',
    'ru',
    'tr',
    'uk',
    'uz',
];

const DEFAULT_LANG_CODE = 'ru';

const DOMAIN_SFX_LANG_MAP: Record<(typeof VALID_DOMAIN_SFX)[number], (typeof VALID_LANG_CODES)[number]> = {
    by: 'be',
    ua: 'uk',
    com: 'en',
    kz: 'kk',
    uz: 'uz',
    'com.tr': 'tr',
};

export function verifyLang(_lang?: string): string {
    const lang = typeof _lang === 'string' ? _lang.toLowerCase() : '';

    if (VALID_LANG_CODES.includes(lang)) {
        return lang;
    }

    return DEFAULT_LANG_CODE;
}

export function langByDomainSfx(domainSfx: string): string {
    if (DOMAIN_SFX_LANG_MAP[domainSfx]) {
        return verifyLang(DOMAIN_SFX_LANG_MAP[domainSfx]);
    }

    return DEFAULT_LANG_CODE;
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const isMethodIdCard = (id: string) => id.startsWith('card-');

export const isMethodIdSbp = (id: string) => id.startsWith('sbp-');

export const isCard = (method: BankCard | SbpToken): method is BankCard => {
    return method.type === PaymentMethodType.card;
};

export const isSbpToken = (method: BankCard | SbpToken): method is SbpToken => {
    return method.type === PaymentMethodType.sbpToken;
};

export const isFrameStartApplePayMessage = (message: PaymentMessage): message is FrameStartApplePayMessage => {
    return message.type === FrameApplePayIncomingMessageType.start;
};

const addMask = (_: string, start: string, end: string) => {
    const mask = '*'.repeat(PAN_SIZE - start.length - end.length);

    return `${start}${mask}${end}`;
};

export const formatCardNumber = (str: string): BankCardNumber =>
    str
        .replace(/\s/g, '')
        .replace(/(\d+)\*+(\d+)/, addMask)
        .replace(/([*\d]{4})/g, '$1 ')
        .trim();

const CAN_BE_DOUBLED_EVENTS = new Set([
    FrameMessageType.escPressed,
    FrameMessageType.sbpBankSelected,
    FrameMessageType.requestOpenUrl,
]);

export const checkIsEventCanBeDoubled = (e: MessageEvent) => e.data?.type && CAN_BE_DOUBLED_EVENTS.has(e.data.type);
